import { Injectable, inject, signal } from '@angular/core';
import { ActivatedRoute, ParamMap, Router, convertToParamMap } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { DataRoomNavigator, DataRoomQueryParams } from './data-room-navigator';

const DATA_ROOM_ROUTE = '/dashboard/market/data-room';

/** O que a aba que hospeda o Data Room mostra (PBI 3800). */
export type DataRoomEmbeddedView =
  | { kind: 'root' }
  | { kind: 'folder'; folderId: string }
  | { kind: 'completeness' };

/** Destino de um link do Data Room, visto de dentro de outra tela. */
export type DataRoomEmbeddedTarget =
  | DataRoomEmbeddedView
  /** Fora do Data Room da empresa (Societário, duplicados, lista de empresas, grupo): abre em nova aba. */
  | { kind: 'external' };

/** Parâmetros da URL da tela hospedeira que guardam a pasta e o documento abertos (decisão do Victor, PBI 3800). */
export const EMBEDDED_FOLDER_PARAM = 'folder';
const EMBEDDED_DOCUMENT_PARAMS = ['doc', 'panel', 'section'] as const;

/**
 * Lê os comandos de rota de um link do Data Room (os mesmos do `router.navigate`) e diz o que a aba mostra.
 * Aceita os comandos inteiros ou partidos (`['/dashboard/market/data-room', 'company-folders-files', id]`).
 * Destino de outra empresa sai em nova aba: a aba só mostra o Data Room da empresa dela.
 */
export function resolveEmbeddedDataRoomTarget(
  commands: unknown[],
  queryParams: DataRoomQueryParams | undefined,
  hostCompanyId: string
): DataRoomEmbeddedTarget {
  const targetCompanyId = queryParams?.['companyId'];
  if (targetCompanyId && targetCompanyId !== hostCompanyId) {
    return { kind: 'external' };
  }
  const path = commands
    .map(part => String(part))
    .join('/')
    .replace(/\/{2,}/g, '/');
  if (!path.startsWith(DATA_ROOM_ROUTE + '/')) {
    return { kind: 'external' };
  }
  const segments = path
    .slice(DATA_ROOM_ROUTE.length + 1)
    .split('/')
    .filter(Boolean);
  const [page, id, sub] = segments;
  if (page === 'company-folders-files' && id && !sub) {
    return { kind: 'folder', folderId: id };
  }
  if (page === 'company-folders' && id === hostCompanyId && sub === 'completude') {
    return { kind: 'completeness' };
  }
  // A raiz chega pelo id da empresa ou pelo id da pasta Root (trilha da pasta, sempre com o `companyId` da aba). Um id
  // qualquer sem a empresa pode ser outra empresa: abre em nova aba, nunca troca de empresa dentro do deal.
  if (page === 'company-folders' && id && !sub && (id === hostCompanyId || !!targetCompanyId)) {
    return { kind: 'root' };
  }
  return { kind: 'external' };
}

/**
 * Navegação do Data Room embutido em outra tela (aba Documentos do deal, PBI 3800), sem iframe.
 *
 * - Pasta, raiz e completude trocam a visão da própria aba ({@link view}); o resto abre o Data Room do mercado em
 *   nova aba.
 * - A pasta e o documento abertos ficam na URL da tela hospedeira (`?folder=&doc=&panel=&section=`, com
 *   `replaceUrl`): F5 e link copiado reabrem o mesmo lugar, e o Voltar do navegador continua saindo da tela.
 * - Os demais parâmetros (`companyId`, `dealView`, `zip`) vivem só em memória: não são da URL da tela hospedeira.
 *
 * Provida no componente que hospeda, junto com {@link configure} (a empresa da aba).
 */
@Injectable()
export class InMemoryDataRoomNavigator implements DataRoomNavigator {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  /** O que a aba mostra agora. */
  readonly view = signal<DataRoomEmbeddedView>({ kind: 'root' });

  private readonly params = new BehaviorSubject<ParamMap>(convertToParamMap({}));
  readonly queryParamMap$ = this.params.asObservable();

  private companyId = '';

  /** Empresa da aba; abre a pasta e o documento que já estão na URL da tela hospedeira. */
  configure(companyId: string): void {
    this.companyId = companyId;
    const hostQuery = this.route.snapshot.queryParamMap;
    const folderId = hostQuery.get(EMBEDDED_FOLDER_PARAM);
    const documentParams: Record<string, string> = {};
    for (const key of EMBEDDED_DOCUMENT_PARAMS) {
      const value = hostQuery.get(key);
      if (value) documentParams[key] = value;
    }
    this.params.next(convertToParamMap({ companyId, dealView: '1', ...documentParams }));
    this.view.set(folderId ? { kind: 'folder', folderId } : { kind: 'root' });
  }

  queryParamMap(): ParamMap {
    return this.params.value;
  }

  patchQueryParams(params: DataRoomQueryParams): void {
    this.params.next(convertToParamMap(mergeParams(paramMapToRecord(this.params.value), params)));
    const hostParams: DataRoomQueryParams = {};
    for (const key of [...EMBEDDED_DOCUMENT_PARAMS, 'documentId']) {
      if (key in params) hostParams[key] = params[key] ?? null;
    }
    if (Object.keys(hostParams).length) {
      this.writeHostUrl(hostParams);
    }
  }

  /** `replaceUrl` não se aplica: a troca de visão já grava a URL da tela hospedeira sem empilhar histórico. */
  navigate(commands: unknown[], queryParams?: DataRoomQueryParams): void {
    const target = resolveEmbeddedDataRoomTarget(commands, queryParams, this.companyId);
    if (target.kind === 'external') {
      const url = this.router.serializeUrl(
        this.router.createUrlTree(commands, { queryParams: queryParams ?? {} })
      );
      window.open(url, '_blank', 'noopener');
      return;
    }

    const documentParams = pickParams(queryParams, EMBEDDED_DOCUMENT_PARAMS);
    this.params.next(
      convertToParamMap({ companyId: this.companyId, dealView: '1', ...documentParams })
    );
    this.view.set(target);
    this.writeHostUrl({
      [EMBEDDED_FOLDER_PARAM]: target.kind === 'folder' ? target.folderId : null,
      doc: documentParams['doc'] ?? null,
      panel: documentParams['panel'] ?? null,
      section: documentParams['section'] ?? null,
      documentId: null,
    });
  }

  /** Voltar, de dentro da aba, é voltar à raiz do Data Room da empresa. */
  back(): void {
    this.navigate([DATA_ROOM_ROUTE, 'company-folders', this.companyId]);
  }

  newTabUrl(): string | null {
    const view = this.view();
    const commands =
      view.kind === 'folder'
        ? [DATA_ROOM_ROUTE, 'company-folders-files', view.folderId]
        : view.kind === 'completeness'
          ? [DATA_ROOM_ROUTE, 'company-folders', this.companyId, 'completude']
          : [DATA_ROOM_ROUTE, 'company-folders', this.companyId];
    const documentParams = pickParams(
      paramMapToRecord(this.params.value),
      EMBEDDED_DOCUMENT_PARAMS
    );
    return this.router.serializeUrl(
      this.router.createUrlTree(commands, {
        queryParams: { companyId: this.companyId, dealView: '1', ...documentParams },
      })
    );
  }

  private writeHostUrl(params: DataRoomQueryParams): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}

function paramMapToRecord(map: ParamMap): Record<string, string> {
  const record: Record<string, string> = {};
  for (const key of map.keys) {
    const value = map.get(key);
    if (value !== null) record[key] = value;
  }
  return record;
}

/** Mescla parâmetros como a query string: `null`/`undefined` apaga a chave. */
function mergeParams(
  current: Record<string, string>,
  changes: DataRoomQueryParams
): Record<string, string> {
  const next = { ...current };
  for (const [key, value] of Object.entries(changes)) {
    if (value === null || value === undefined) delete next[key];
    else next[key] = value;
  }
  return next;
}

function pickParams(
  params: DataRoomQueryParams | undefined,
  keys: readonly string[]
): Record<string, string> {
  const picked: Record<string, string> = {};
  for (const key of keys) {
    const value = params?.[key];
    if (value) picked[key] = value;
  }
  return picked;
}
